import type { CertMember } from 'src/types/Certification';

export interface DuplicateContact {
  type: 'email' | 'phone';
  value: string;
  cert_member_ids: number[];
}

const PLACEHOLDER_EMAIL_SUFFIX = '@prontohousing.io';

/**
 * Normalize an email for duplicate comparison: trim, lowercase, and drop
 * system-generated `@prontohousing.io` placeholder addresses.
 */
export function normalizeEmail(
  email: string | null | undefined
): string | null {
  const value = (email ?? '').trim().toLowerCase();
  if (!value || value.endsWith(PLACEHOLDER_EMAIL_SUFFIX)) {
    return null;
  }
  return value;
}

/**
 * Normalize a phone for duplicate comparison: strip non-digits and drop the
 * leading country code `1` for 11-digit US numbers.
 */
export function normalizePhone(
  phone: string | null | undefined
): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
}

/**
 * Return every email/phone shared by more than one household member.
 */
export function getDuplicateContacts(
  members: CertMember[]
): DuplicateContact[] {
  const emailOwners = new Map<string, number[]>();
  const phoneOwners = new Map<string, number[]>();

  for (const member of members) {
    for (const entry of member.emails ?? []) {
      const normalized = normalizeEmail(entry.email);
      if (!normalized) {
        continue;
      }
      const owners = emailOwners.get(normalized) ?? [];
      owners.push(member.cert_member_id);
      emailOwners.set(normalized, owners);
    }
    for (const entry of member.phones ?? []) {
      const normalized = normalizePhone(entry.phone_number);
      if (!normalized) {
        continue;
      }
      const owners = phoneOwners.get(normalized) ?? [];
      owners.push(member.cert_member_id);
      phoneOwners.set(normalized, owners);
    }
  }

  const duplicates: DuplicateContact[] = [];
  for (const [value, ids] of emailOwners) {
    if (ids.length > 1) {
      duplicates.push({ type: 'email', value, cert_member_ids: ids });
    }
  }
  for (const [value, ids] of phoneOwners) {
    if (ids.length > 1) {
      duplicates.push({ type: 'phone', value, cert_member_ids: ids });
    }
  }
  return duplicates;
}

/**
 * Return the first sibling member whose email/phone matches the provided
 * values, or `null`. `excludeCertMemberId` skips the member being edited so a
 * member never conflicts with itself.
 */
export function findMemberContactConflict(
  members: CertMember[],
  email: string | null | undefined,
  phone: string | null | undefined,
  excludeCertMemberId?: number | null
): DuplicateContact | null {
  const normalizedEmail = normalizeEmail(email);
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedEmail && !normalizedPhone) {
    return null;
  }

  for (const member of members) {
    if (member.cert_member_id === excludeCertMemberId) {
      continue;
    }
    for (const entry of member.emails ?? []) {
      if (normalizedEmail && normalizeEmail(entry.email) === normalizedEmail) {
        return {
          type: 'email',
          value: normalizedEmail,
          cert_member_ids: [member.cert_member_id],
        };
      }
    }
    for (const entry of member.phones ?? []) {
      if (
        normalizedPhone &&
        normalizePhone(entry.phone_number) === normalizedPhone
      ) {
        return {
          type: 'phone',
          value: normalizedPhone,
          cert_member_ids: [member.cert_member_id],
        };
      }
    }
  }
  return null;
}

/**
 * Return the set of cert_member_ids that share a duplicate email/phone.
 */
export function getDuplicateContactMemberIds(
  members: CertMember[]
): Set<number> {
  const ids = new Set<number>();
  for (const duplicate of getDuplicateContacts(members)) {
    duplicate.cert_member_ids.forEach((id) => ids.add(id));
  }
  return ids;
}
